import React, { useState } from 'react';
import {
  PRIORITIES, SOUNDS, PARAM_OPTIONS, TECH_CONDITIONS,
  getAlarmConfig, saveAlarmConfig, defaultConfig, sanitizeRule, repeatRule,
} from './alarmConfig';
import { armAudio, playBurst, isMuted } from './alertSound';

const GROUPS = [
  { key: 'vital',      title: 'Vital-sign limits',  hint: 'Heart rate, SpO2, respiration, temperature.', add: 'limit',  addLabel: '+ Add limit' },
  { key: 'arrhythmia', title: 'Arrhythmia events',  hint: 'Rhythm calls from the analysis (VT, AFIB, PAUSE …). The code must match what the analysis sends.', add: 'event', addLabel: '+ Add arrhythmia' },
  { key: 'technical',  title: 'Technical alarms',   hint: 'Electrodes, device, connection and batteries.', add: 'limit', addLabel: '+ Add battery limit' },
];

function newRule(group, kind) {
  if (kind === 'event') {
    return sanitizeRule({ kind: 'event', group: 'arrhythmia', label: 'NEW ARRHYTHMIA', event: 'NEWEVENT',
      delaySec: 0, priority: 'medium', sound: 'cardiac' });
  }
  const param = group === 'technical' ? 'ecgBatt' : 'hr';
  return sanitizeRule({ kind: 'limit', group: group, label: 'NEW ALARM', param: param,
    op: group === 'technical' ? 'lt' : 'gt', value: group === 'technical' ? 15 : 130,
    delaySec: 10, priority: 'medium', sound: (PARAM_OPTIONS.find(p => p.key === param) || {}).sound });
}

/**
 * Alarm settings — every alarm's message, condition, persistence delay,
 * priority and sound. Edits are a draft until Save; Save applies to everyone.
 */
export default function AlarmSettings({ onClose }) {
  const [cfg] = useState(getAlarmConfig);
  const [rules, setRules] = useState(() => cfg.rules.map(r => Object.assign({}, r)));
  const [dirty, setDirty] = useState(false);
  const [note, setNote] = useState('');
  const [saving, setSaving] = useState(false);

  const patch = (id, change) => {
    setRules(prev => prev.map(r => {
      if (r.id !== id) return r;
      const next = Object.assign({}, r, change);
      // Changing priority moves the repeat interval to that priority's default;
      // changing the parameter picks that parameter's category sound.
      if (change.priority && change.priority !== r.priority) next.repeatSec = PRIORITIES[change.priority].repeatDefault;
      if (change.param && change.param !== r.param) next.sound = (PARAM_OPTIONS.find(p => p.key === change.param) || {}).sound || next.sound;
      return next;
    }));
    setDirty(true); setNote('');
  };
  const remove = (id) => { setRules(prev => prev.filter(r => r.id !== id)); setDirty(true); setNote(''); };
  const add = (group, kind) => { setRules(prev => prev.concat([newRule(group, kind)])); setDirty(true); setNote(''); };
  const reset = () => { setRules(defaultConfig().rules); setDirty(true); setNote('Defaults loaded — press Save to apply them.'); };
  const test = (r) => {
    armAudio();
    if (isMuted()) { setNote('Sound is muted — unmute to hear the test.'); return; }
    // armAudio resumes asynchronously on the first gesture; give it a tick.
    setTimeout(() => playBurst(r.priority, r.sound), 60);
  };
  const save = () => {
    setSaving(true);
    saveAlarmConfig({ rules: rules }).then(res => {
      setSaving(false); setDirty(false);
      setRules(getAlarmConfig().rules.map(r => Object.assign({}, r)));   // show the clamped values
      setNote(res.shared
        ? 'Saved. Applies to every Live Monitor screen.'
        : 'Saved on this computer only — the server did not accept it (not deployed yet, or you are offline).');
    });
  };

  const num = (v) => (v === '' || v == null ? '' : v);

  return (
    <section className="lm-as" aria-label="Alarm settings">
      <header className="lm-as-head">
        <div>
          <h2 className="lm-as-title">Alarm settings</h2>
          <div className="lm-as-sub">
            Priorities, indicators and sounds follow IEC 60601-1-8.
            {cfg.rev ? ` Revision ${cfg.rev}${cfg.updatedBy ? ` by ${cfg.updatedBy}` : ''}${cfg.updatedAt ? `, ${new Date(cfg.updatedAt).toLocaleString()}` : ''}.` : ' Using the default set.'}
          </div>
        </div>
        <div className="lm-as-actions">
          <button type="button" className="lm-as-btn" onClick={reset}>Reset to defaults</button>
          <button type="button" className="lm-as-btn" onClick={onClose}>{dirty ? 'Discard' : 'Close'}</button>
          <button type="button" className="lm-as-btn lm-as-primary" onClick={save} disabled={!dirty || saving}>
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      </header>

      {note && <div className="lm-as-note" role="status">{note}</div>}

      <div className="lm-as-legend">
        {Object.keys(PRIORITIES).map(k => (
          <span key={k} className={`lm-as-legend-item lm-as-pri-${k}`}>
            <span className="lm-alert-mark">{PRIORITIES[k].mark}</span>
            <strong>{PRIORITIES[k].label}</strong> · {PRIORITIES[k].colour} · {PRIORITIES[k].burst}
          </span>
        ))}
      </div>

      {GROUPS.map(g => {
        const list = rules.filter(r => r.group === g.key);
        return (
          <div key={g.key} className="lm-as-group">
            <div className="lm-as-group-head">
              <div>
                <h3 className="lm-as-group-title">{g.title}</h3>
                <div className="lm-as-hint">{g.hint}</div>
              </div>
              <button type="button" className="lm-as-btn" onClick={() => add(g.key, g.add)}>{g.addLabel}</button>
            </div>
            <div className="lm-as-scroll">
              <table className="lm-as-table">
                <thead>
                  <tr>
                    <th>On</th><th>Alarm message</th><th>Condition</th>
                    <th title="Alarm condition delay: the condition must persist this long before the alarm is raised">Delay&nbsp;s</th>
                    <th>Priority</th>
                    <th title="Untick to mute THIS alarm for everyone: it still shows, blinks and is recorded, but makes no sound">Audible</th>
                    <th>Sound</th>
                    <th title="Seconds between repeated bursts. High 2.5–15, medium 2.5–30, low 0 (once) or 15+">Repeat&nbsp;s</th>
                    <th title="Stop the SOUND after this many seconds (the alarm stays visible). 0 = until it clears or is acknowledged">Sound&nbsp;limit&nbsp;s</th>
                    <th>Test</th><th></th>
                  </tr>
                </thead>
                <tbody>
                  {list.map(r => {
                    const P = PRIORITIES[r.priority];
                    const unit = r.kind === 'limit' ? ((PARAM_OPTIONS.find(p => p.key === r.param) || {}).unit || '') : '';
                    return (
                      <tr key={r.id} className={r.enabled ? '' : 'lm-as-off'}>
                        <td><input type="checkbox" checked={r.enabled} aria-label={`Enable ${r.label}`}
                          onChange={e => patch(r.id, { enabled: e.target.checked })} /></td>
                        <td><input className="lm-as-in lm-as-in-msg" value={r.label} maxLength={32} aria-label="Alarm message"
                          onChange={e => patch(r.id, { label: e.target.value.toUpperCase() })} /></td>
                        <td>
                          {r.kind === 'limit' && (
                            <span className="lm-as-cond">
                              <select className="lm-as-in" value={r.param} aria-label="Parameter"
                                onChange={e => patch(r.id, { param: e.target.value })}>
                                {PARAM_OPTIONS.filter(p => (g.key === 'technical') === /Batt$/.test(p.key)).map(p =>
                                  <option key={p.key} value={p.key}>{p.label}</option>)}
                              </select>
                              <select className="lm-as-in lm-as-in-op" value={r.op} aria-label="Comparison"
                                onChange={e => patch(r.id, { op: e.target.value })}>
                                <option value="gt">above</option>
                                <option value="lt">below</option>
                              </select>
                              <input className="lm-as-in lm-as-in-num" type="number" step="any" value={num(r.value)} aria-label="Threshold"
                                onChange={e => patch(r.id, { value: e.target.value })} />
                              <span className="lm-as-unit">{unit}</span>
                            </span>
                          )}
                          {r.kind === 'event' && (
                            <span className="lm-as-cond">
                              <span className="lm-as-unit">event code</span>
                              <input className="lm-as-in lm-as-in-code" value={r.event} maxLength={24} aria-label="Event code"
                                onChange={e => patch(r.id, { event: e.target.value.toUpperCase().replace(/[^A-Z0-9]/g, '') })} />
                            </span>
                          )}
                          {r.kind === 'technical' && <span className="lm-as-fixed">{(TECH_CONDITIONS[r.tech] || {}).name}</span>}
                        </td>
                        <td><input className="lm-as-in lm-as-in-num" type="number" min="0" max="600" value={num(r.delaySec)} aria-label="Delay seconds"
                          disabled={r.kind === 'technical' && r.tech !== 'lead_off'}
                          title={r.kind === 'technical' && r.tech !== 'lead_off' ? 'Fixed: decided from the app heartbeat (6 s / 15 s)' : ''}
                          onChange={e => patch(r.id, { delaySec: e.target.value })} /></td>
                        <td>
                          <select className={`lm-as-in lm-as-pri lm-as-pri-${r.priority}`} value={r.priority} aria-label="Priority"
                            onChange={e => patch(r.id, { priority: e.target.value })}>
                            {Object.keys(PRIORITIES).map(k => <option key={k} value={k}>{PRIORITIES[k].mark} {PRIORITIES[k].label}</option>)}
                          </select>
                        </td>
                        <td className="lm-as-center">
                          <input type="checkbox" checked={r.audible !== false} aria-label={`${r.label} makes a sound`}
                            title={r.audible !== false ? 'This alarm sounds. Untick to mute it for everyone.' : 'Muted for everyone — shows but never sounds.'}
                            onChange={e => patch(r.id, { audible: e.target.checked })} />
                        </td>
                        <td>
                          <select className="lm-as-in" value={r.sound} aria-label="Sound"
                            onChange={e => patch(r.id, { sound: e.target.value })}>
                            {Object.keys(SOUNDS).map(k => <option key={k} value={k}>{SOUNDS[k].label}</option>)}
                          </select>
                        </td>
                        <td>
                          <input className="lm-as-in lm-as-in-num" type="number" step="0.5"
                            min={r.priority === 'low' ? 0 : P.repeat[0]} max={P.repeat[1]} value={num(r.repeatSec)} aria-label="Repeat seconds"
                            disabled={r.audible === false}
                            onChange={e => patch(r.id, { repeatSec: e.target.value })} />
                          {/* Say what the number MEANS and what it will become — never change it silently. */}
                          {(() => {
                            if (r.audible === false) return <div className="lm-as-rep">muted</div>;
                            const rr = repeatRule(r.priority, r.repeatSec);
                            return <div className={`lm-as-rep${rr.clamped ? ' lm-as-rep-warn' : ''}`}>{rr.clamped ? `→ ${rr.value} s: ${rr.text}` : rr.text}</div>;
                          })()}
                        </td>
                        <td><input className="lm-as-in lm-as-in-num" type="number" min="0" max="3600" value={num(r.maxSoundSec)} aria-label="Sound limit seconds"
                          onChange={e => patch(r.id, { maxSoundSec: e.target.value })} /></td>
                        <td><button type="button" className="lm-as-btn lm-as-test" onClick={() => test(r)} title="Play this alarm's sound">▶</button></td>
                        <td>{r.kind !== 'technical' && (
                          <button type="button" className="lm-as-btn lm-as-del" onClick={() => remove(r.id)}
                            aria-label={`Delete ${r.label}`} title="Delete this alarm">✕</button>
                        )}</td>
                      </tr>
                    );
                  })}
                  {list.length === 0 && <tr><td colSpan={11} className="lm-as-empty">No alarms in this group.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </section>
  );
}
